import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");

  return {
    headers: {
      Authorization: `Bearer ${storedUser?.accessToken}`,
    },
  };
};

const getError = (error, fallback) =>
  error?.response?.data?.Exception ||
  error?.response?.data?.message ||
  fallback;

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatMonth = (month) => {
  if (!month) return "-";

  const [year, monthNumber] = month.split("-").map(Number);

  const date = new Date(year, monthNumber - 1, 1);

  return date.toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AllocationDetails = () => {
  const { allocationId } = useParams();
  const navigate = useNavigate();

  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAllocation = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${API}/production-allocation/${allocationId}`,
        authHeader(),
      );

      setAllocation(response.data);
    } catch (err) {
      setAllocation(null);

      setError(getError(err, "Unable to load the allocation details."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllocation();
  }, [allocationId]);

  const lines = allocation?.lines || [];

  const totalQty = useMemo(() => {
    return lines.reduce(
      (total, line) => total + Number(line.allocatedQty || 0),
      0,
    );
  }, [lines]);

  const isAllotted = allocation?.status === "ALLOTTED";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        px: {
          xs: 1.5,
          sm: 2.5,
          md: 3,
        },
        py: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(-1)}
        sx={{
          mb: 1.75,
          textTransform: "none",
          color: "#475467",
          fontWeight: 600,
        }}
      >
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e4e7ec",
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              py: 10,
              textAlign: "center",
            }}
          >
            <CircularProgress size={28} />

            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                color: "#667085",
              }}
            >
              Loading allocation details...
            </Typography>
          </Box>
        </Paper>
      ) : !allocation ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e4e7ec",
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              py: 8,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#475467",
              }}
            >
              Allocation not found
            </Typography>
          </Box>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: {
                xs: "flex-start",
                md: "center",
              },
              gap: 2,
              mb: 2.5,
              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {isAllotted ? (
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 27,
                      color: "#027a48",
                    }}
                  />
                ) : (
                  <BookmarkBorderOutlinedIcon
                    sx={{
                      fontSize: 27,
                      color: "#b54708",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    fontSize: {
                      xs: 24,
                      md: 28,
                    },
                    fontWeight: 700,
                    color: "#172033",
                  }}
                >
                  {isAllotted ? "Allotment Details" : "Reserve Details"}
                </Typography>

                <Chip
                  label={isAllotted ? "Allotted" : "Reserved"}
                  size="small"
                  sx={{
                    height: 23,
                    backgroundColor: isAllotted ? "#ecfdf3" : "#fffaeb",
                    color: isAllotted ? "#027a48" : "#b54708",
                    fontWeight: 600,
                    fontSize: 10,
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 13,
                  color: "#667085",
                }}
              >
                {allocation.allocationId}
              </Typography>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e4e7ec",
              borderRadius: 2,
              backgroundColor: "#fff",
              mb: 2,
            }}
          >
            <Box
              sx={{
                p: {
                  xs: 1.75,
                  md: 2.25,
                },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  md: "repeat(5, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <InfoItem label="Month" value={formatMonth(allocation.month)} />

              <InfoItem label="Report" value={allocation.reportId || "-"} />

              <InfoItem label="Total Quantity" value={formatNumber(totalQty)} />

              <InfoItem
                label="Customers"
                value={formatNumber(allocation.customerCount)}
              />

              <InfoItem
                label="Items"
                value={formatNumber(allocation.itemCount)}
              />
            </Box>

            <Divider />

            <Box
              sx={{
                p: {
                  xs: 1.75,
                  md: 2.25,
                },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <InfoItem
                label="Created By"
                value={allocation.createdBy || "-"}
              />

              <InfoItem
                label="Created On"
                value={formatDate(allocation.createdDate)}
              />

              <InfoItem
                label="Allotted By"
                value={allocation.allottedBy || "-"}
              />

              <InfoItem
                label="Allotted On"
                value={formatDate(allocation.allottedDate)}
              />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e4e7ec",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 1.75,
                  md: 2.25,
                },
                py: 1.75,
                borderBottom: "1px solid #eaecf0",
              }}
            >
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#344054",
                }}
              >
                Customer Allocations
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: 12,
                  color: "#667085",
                }}
              >
                Quantity allocated for each sales requirement in this batch.
              </Typography>
            </Box>

            {!lines.length ? (
              <Box
                sx={{
                  py: 7,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#667085",
                  }}
                >
                  No allocation lines found.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: "calc(100vh - 390px)",
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 950 }}>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>Priority</HeaderCell>
                      <HeaderCell>Customer</HeaderCell>
                      <HeaderCell>Item</HeaderCell>
                      <HeaderCell>Sales Item</HeaderCell>
                      <HeaderCell align="right">Plan Qty</HeaderCell>
                      <HeaderCell align="right">Min Qty</HeaderCell>
                      <HeaderCell align="right">Max Qty</HeaderCell>
                      <HeaderCell align="right">Allocated</HeaderCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {lines.map((line) => (
                      <TableRow
                        key={line.allocationLineId}
                        sx={{
                          "&:last-child td": {
                            borderBottom: 0,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#475467",
                            }}
                          >
                            {line.priority ?? "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Cell value={line.customer || "-"} strong />
                        </TableCell>

                        <TableCell>
                          <Cell value={line.itemCode || "-"} mono />
                        </TableCell>

                        <TableCell>
                          <Cell value={line.salesItemId || "-"} mono />
                        </TableCell>

                        <TableCell align="right">
                          <Cell value={formatNumber(line.planQty)} />
                        </TableCell>

                        <TableCell align="right">
                          <Cell value={formatNumber(line.minQty)} />
                        </TableCell>

                        <TableCell align="right">
                          <Cell value={formatNumber(line.maxQty)} />
                        </TableCell>

                        <TableCell align="right">
                          <Cell
                            value={formatNumber(line.allocatedQty)}
                            strong
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color: "#667085",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        mt: 0.35,
        fontSize: 13,
        fontWeight: 600,
        color: "#344054",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const HeaderCell = ({ children, align = "left" }) => (
  <TableCell
    align={align}
    sx={{
      backgroundColor: "#f8fafc",
      color: "#667085",
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      whiteSpace: "nowrap",
      borderBottom: "1px solid #eaecf0",
    }}
  >
    {children}
  </TableCell>
);

const Cell = ({ value, strong, mono }) => (
  <Typography
    title={value}
    sx={{
      fontSize: 12,
      fontWeight: strong ? 600 : 400,
      color: "#344054",
      fontFamily: mono ? "IBM Plex Mono, monospace" : "inherit",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: 220,
    }}
  >
    {value}
  </Typography>
);

export default AllocationDetails;
